import _                                  from 'underscore';
import React, { FC, useEffect, useState } from 'react';
import { useQuery }                       from '@apollo/react-hooks';

import { CorvalCategory, CorvalContextOwner, CorvalStaticProjectActivity, Uuid } from '@src/types';
import { queriesHaveData, queriesLoading }                                       from '@src/store/_apolloUtil';
import { formatOption, IFormatOptionValue }                                      from '@src/util';
import { QueriedSelectFieldProps, SelectField }                                  from '@src/views/components/forms';
import { ActivityQueries }                                                       from "@src/queries/ActivityQueries";
import { DecisionEngineQueries }                                                 from "@src/queries";


interface CategoryDropdownProps extends QueriedSelectFieldProps {
    projectId: Uuid,
    projectVersionId: Uuid,
    loading?: boolean,
    formatOptions?: object,
}

const defaultProps: CategoryDropdownProps = {
    projectId:        null,
    projectVersionId: null,
    loading:          false,
    formatOptions:    {}
};

const ProjectCategoryDropdown: FC<CategoryDropdownProps> = ( _props ) => {

    let { formatOptions, projectVersionId, projectId, ...props } = _props;

    const [ categories, setCategories ] = useState<IFormatOptionValue[]>( [] );

    const getAvailableCategoryReq = useQuery<{ categories: CorvalCategory[] }>( DecisionEngineQueries.queries.GET_CATEGORIES( `id name` ), {
        fetchPolicy: 'network-only',
        variables:   {
            where: {
                or: [
                    { contextOwner: { eq: CorvalContextOwner.Corval } },
                    { contextOwnerProjectId: { eq: projectId } }
                ]
            }
        }
    } );

    const getStaticProjectActivitiesReq = useQuery<{ staticProjectActivities: CorvalStaticProjectActivity[] }>( ActivityQueries.queries.GET_PROJECT_ACTIVITIES( `id categoryId categoryName subCategoryId subCategoryName`, projectVersionId ), {
        variables:   { projectVersionId },
        skip:        ( !projectId || !projectVersionId ),
        fetchPolicy: 'network-only'
    } );

    const isLoading = ( props.loading || queriesLoading( [ getAvailableCategoryReq, getStaticProjectActivitiesReq ] ) );

    useEffect( () => {

        if( queriesHaveData( [ getAvailableCategoryReq, getStaticProjectActivitiesReq ] ) ){

            let activeCatIds = [];


            const activityCategoryOptions = _.chain( getStaticProjectActivitiesReq.data.staticProjectActivities )
                                             .map( item => formatOption( item.categoryId, item.categoryName ) )
                                             .uniq( item => ( item.value ) )
                                             .tap( items => {
                                                 activeCatIds = _.pluck( items, 'value' );
                                             } )
                                             .value();

            const availableCategoryOptions = _.chain( getAvailableCategoryReq.data.categories )
                                              .filter( item => !activeCatIds.includes( item.id ) )
                                              .map( item => formatOption( item.id, item.name ) )
                                              .uniq( item => ( item.value ) )
                                              .value();

            setCategories( _.sortBy( _.union( availableCategoryOptions, activityCategoryOptions ), item => ( item.text ) ) );
        }

    }, [ getAvailableCategoryReq, getStaticProjectActivitiesReq ] );

    return (
        <SelectField
            { ...props }
            search
            loading={ isLoading }
            options={ categories }
        />
    );
};

ProjectCategoryDropdown.defaultProps = defaultProps;

export { ProjectCategoryDropdown };
